import { useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { useWorkSampleSelection } from "@/components/shared/useWorkSampleSelection";

const MAX_SAMPLES = 3;

/**
 * Collaboration Opportunities Phase 2 (2026-08) — the Apply flow. References the applicant's
 * OWN, EXISTING `freelancer_work_samples` (never a re-upload) via GET /v1/build/my-work-samples
 * (already scoped freelancer-only). Never asks for a rate, fee, or any commercial field.
 */
export function ApplyToSlotModal({ opportunityId, slotId, roleLabel, compensation, kind, onClose, onApplied }) {
  const { samples, selected, toggle } = useWorkSampleSelection(MAX_SAMPLES);
  const [note, setNote] = useState("");
  const [workUrl, setWorkUrl] = useState("");
  const [workNote, setWorkNote] = useState("");
  const [availability, setAvailability] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const showDisclaimer = compensation === "paid" && kind === "member";

  const submit = async () => {
    const trimmedUrl = workUrl.trim();
    if (selected.length === 0 && !trimmedUrl) { toast.error("Pick a work sample or share a work link"); return; }
    if (note.length > 500) { toast.error("Keep your note under 500 characters"); return; }
    if (workNote.length > 300) { toast.error("Keep your work note under 300 characters"); return; }
    if (availability.length > 200) { toast.error("Keep your availability note under 200 characters"); return; }
    setSubmitting(true);
    try {
      await api.post(`/v1/collaboration/opportunities/${opportunityId}/slots/${slotId}/apply`, {
        note: note.trim() || null, work_sample_ids: selected,
        application_work_url: trimmedUrl || null, work_note: workNote.trim() || null,
        availability: availability.trim() || null,
      });
      toast.success("Application sent");
      onApplied();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "already_applied") toast.error("You already applied to this role");
      else if (code === "slot_full") toast.error("This role just filled up");
      else if (code === "freelancer_profile_required") toast.error("A Freelancer Profile is required to apply");
      else if (code === "invalid_work_samples") toast.error("Pick up to 3 of your own work samples");
      else if (code === "invalid_work_url") toast.error("That work link doesn't look valid");
      else if (code === "work_reference_required") toast.error("Pick a work sample or share a work link");
      else if (code === "field_too_long" && e?.response?.data?.detail?.field === "availability") toast.error("Keep your availability note shorter");
      else toast.error("Couldn't submit your application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 p-4" onClick={onClose} data-testid="apply-to-slot-overlay">
      <div className="w-full max-w-md rounded-2xl bg-paper shadow-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 bg-ink border-t-[3px] border-brand flex items-center justify-between px-5 py-4">
          <span className="font-mont text-sm font-bold text-paper">Apply — {roleLabel}</span>
          <button onClick={onClose} data-testid="apply-to-slot-close" className="text-paper/60 hover:text-paper transition-colors text-lg leading-none">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Pick up to 3 work samples to show (optional)</label>
            {samples === null ? (
              <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-faint" /></div>
            ) : samples.length === 0 ? (
              <div className="mt-2 font-mont text-xs text-faint" data-testid="apply-to-slot-no-samples">No saved work samples yet — share a work link below instead, or add samples to your Profile first.</div>
            ) : (
              <div className="mt-2 space-y-1.5" data-testid="apply-to-slot-samples-list">
                {samples.map((s) => (
                  <label key={s.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 font-mont text-xs cursor-pointer transition-colors ${selected.includes(s.id) ? "border-brand bg-[#FFFCF2]" : "border-black/10"}`} data-testid={`apply-to-slot-sample-${s.id}`}>
                    <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} className="accent-brand" />
                    <span className="truncate text-ink-soft">{s.title || s.url}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Relevant work for this {kind === "njm" ? "NJM Collaboration" : "Member Collaboration"} — optional</label>
            <p className="mt-1 font-mont text-[11px] text-faint leading-relaxed">Share one piece of work that's particularly relevant to this {kind === "njm" ? "NJM Collaboration" : "Member Collaboration"} — it won't be added to your Profile.</p>
            <input value={workUrl} onChange={(e) => setWorkUrl(e.target.value)} type="url" placeholder="https://…" data-testid="apply-to-slot-work-url"
              className="mt-1.5 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors" />
            {workUrl.trim() && (
              <input value={workNote} onChange={(e) => setWorkNote(e.target.value)} placeholder="Work note — optional, e.g. “I edited and graded this film.”" data-testid="apply-to-slot-work-note"
                className="mt-2 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors" />
            )}
          </div>
          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Availability (optional)</label>
            <input value={availability} onChange={(e) => setAvailability(e.target.value)} placeholder="e.g. Weekends only, or free from Oct 15" data-testid="apply-to-slot-availability"
              className="mt-1.5 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors" />
          </div>
          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Note to the creator (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why you'd be a good fit…" data-testid="apply-to-slot-note"
              className="mt-1.5 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors resize-none" />
          </div>
          {showDisclaimer && (
            <p data-testid="apply-to-slot-disclaimer" className="rounded-lg bg-surface2/60 px-3.5 py-2.5 font-mont text-[11px] text-ink-soft leading-relaxed">
              Payment is arranged directly by the Member Collaboration's creator. NJM is not a party to the payment.
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center justify-between gap-2 border-t border-black/8 bg-surface2/40 px-5 py-4">
          <button onClick={onClose} data-testid="apply-to-slot-cancel" className="font-mont text-xs font-semibold text-ink-soft hover:text-ink transition-colors">Cancel</button>
          <button onClick={submit} disabled={submitting || samples === null} data-testid="apply-to-slot-submit"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 font-mont text-xs font-bold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send Application
          </button>
        </div>
      </div>
    </div>
  );
}
